import { useCallback, useEffect, useState } from 'react';
import type { UserCustomizationSettings } from '../types/UserCustomization';
import {
  getCustomization,
  saveCustomization,
  resetCustomization,
} from '../api/userCustomization';

// Per-resource customization hook. NOT module-level cached (unlike the
// reference-data hooks) — each LopCrudPage instance owns its customization
// and we want fresh fetches when navigating between screens.
//
// Returns:
//   data    — UserCustomizationSettings | null (null = no row, use defaults)
//   loading — initial fetch in progress
//   error   — error message (null if OK)
//   save    — persist + update local state
//   reset   — DELETE row + clear local state
interface UseUserCustomizationResult {
  data: UserCustomizationSettings | null;
  loading: boolean;
  error: string | null;
  save: (next: UserCustomizationSettings) => Promise<void>;
  reset: () => Promise<void>;
}

export function useUserCustomization(
  resourceCode: string,
): UseUserCustomizationResult {
  const [data, setData] = useState<UserCustomizationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCustomization(resourceCode)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resourceCode]);

  const save = useCallback(
    async (next: UserCustomizationSettings) => {
      await saveCustomization(resourceCode, next);
      setData(next);
    },
    [resourceCode],
  );

  const reset = useCallback(async () => {
    await resetCustomization(resourceCode);
    setData(null);
  }, [resourceCode]);

  return { data, loading, error, save, reset };
}
